var __decorate = (this && this.__decorate) || function (decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for (var i = decorators.length - 1; i >= 0; i--) if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
};
import readyState from 'ready-state';
import Injector, { InjectorScope } from './injector';
import { name as callbackQueueName } from '../queues/callback/callbackQueue';
import { inject } from '../decorators/injectable';
import ComponentRegistry from './componentRegistry';
import { applyDefaultOptions, hasDefaultOptions, } from '../decorators/defaultOptions';
import { registerable } from '../decorators/registerable';
import { awaitProperty } from '../utilities/awaitProperty';
import { getModuleName } from '../decorators/catalogItem';
/** The name of the module */
export const name = 'CatalogItem';
// CallbackQueue key for the init callbacks
const InitQueueKey = 'init';
// Dependencies for method-level injection of the init functions
const queueDependencies = {
    ...Injector.createDependency(callbackQueueName, InjectorScope.Singleton),
};
let CatalogItem = class CatalogItem {
    /**
     * Create a catalog item
     *
     * @param {Object} options Data and options to initialize the component with
     */
    constructor(options = {}) {
        this.options = options;
        // Are all the initialization calls completed for this component?
        this.hasBeenInitialized = false;
        // If the object has default options defined, then merge them with the specified options
        // Only do this if the object has keys (options to apply)
        this.options = hasDefaultOptions(this) ? applyDefaultOptions(this, options) : options;
        Object.freeze(this.options);
        // Since init() is expected to do DOM traversal/manipulation, defer it until
        // the page is loaded
        readyState.domready
            .then(this.init.bind(this))
            .then(this.registerElement.bind(this, getModuleName(this.constructor)))
            .then(this.processInitQueue.bind(this))
            .then(this.initComplete.bind(this));
    }
    /**
       * Placeholder method for extending classes to do any DOM/module initialization
       */
    init() {
        this.hasBeenInitialized = false;
    }
    /**
       * Process all callbacks (if any) in the init queue
       */
    async processInitQueue(callbackQueue) {
        await callbackQueue.fire(this.constructor.name, InitQueueKey, this);
    }
    initComplete() {
        this.hasBeenInitialized = true;
    }
    /**
     * Perform any required cleanup to properly destroy the component
     *
     * @param cleanDom Should dynamically-inserted DOM elements be removed?
     */
    destroy(cleanDom) { } // eslint-disable-line class-methods-use-this, @typescript-eslint/no-unused-vars
    async isInitialized() {
        try {
            await awaitProperty('hasBeenInitialized', this);
            return true;
        }
        catch (e) {
            return false;
        }
    }
    /**
     * Register a module/component for reverse lookup by an HTML element
     *
     * @param moduleName Name of the component being registered
     */
    registerElement(moduleName) {
        if (this.element) {
            ComponentRegistry.add(this.element, moduleName, this);
        }
    }
    /**
       * Add a callback function that will be executed as part of the component init process
       *
       * @param moduleName Name of the module to add the callback to
       * @param callback The callback function to execute
       */
    static addToInitQueue(moduleName, callback, callbackQueue) {
        callbackQueue.add(moduleName, InitQueueKey, callback);
    }
    /**
     * Asynchronous function for waiting until 'this.element' is available
     */
    async isElementAvailable() {
        try {
            await awaitProperty('element', this);
            return true;
        }
        catch (e) {
            return false;
        }
    }
    /**
     * Get the HTML element used by the component
     */
    getElement() {
        return this.element;
    }
    // Disabling the any here because the options can be of any value
    static async loadElement(moduleName, options) {
        await readyState.domready;
        if (typeof options.element === 'object' && options.element instanceof HTMLElement) {
            return options.element;
        }
        if (typeof options.id !== 'string') {
            return undefined;
        }
        const element = document.getElementById(options.id);
        return element;
    }
};
__decorate([
    inject(queueDependencies)
], CatalogItem.prototype, "processInitQueue", null);
__decorate([
    inject(queueDependencies)
], CatalogItem, "addToInitQueue", null);
CatalogItem = __decorate([
    registerable
], CatalogItem);
export default CatalogItem;
